import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, AlertTriangle, Download, FileText } from 'lucide-react';
import CanAccess from '@/components/ui/CanAccess';
import DrillBreadcrumb from '@/components/ui/DrillBreadcrumb';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import ALCOABadge from '@/components/ui/ALCOABadge';
import AuditTrail from '@/components/ui/AuditTrail';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import { format, differenceInDays } from 'date-fns';
import { useTableExport } from '@/hooks/useTableExport';

interface Capa {
  id: string;
  capaCode: string;
  source: string;
  product: string;
  batchNo: string;
  severity: string;
  status: string;
  department: string;
  owner?: { name: string };
  dueDate: string;
  daysOverdue: number;
  title: string;
  description: string;
  rootCause: string;
  actionPlan: string;
  createdAt: string;
  createdBy?: { name: string; role: string };
  updatedBy?: { name: string; role: string };
  updatedAt?: string;
  auditTrail?: { id: string; action: string; user: string; role: string; timestamp: string; note?: string; type: 'create' | 'update' | 'approve' | 'close' | 'reject' | 'review' }[];
  escalationLevel?: number;
  escalationHistory?: { level: number; escalatedBy: string; escalatedAt: string; reason: string }[];
  effectivenessCheck?: { scheduledDate?: string; status?: string; notes?: string; completedAt?: string };
}

const MOCK_CAPAS: Capa[] = [
  // 2023
  {
    id: 'mock-capa-2301', capaCode: 'CAPA-2023-001', source: 'Regulatory Inspection',
    product: 'Amoxicillin 500mg Capsules', batchNo: 'BT-AMX-2301',
    severity: 'critical', status: 'closed', department: 'Quality Assurance',
    owner: { name: 'Dr. Anjali Mehta' }, dueDate: '2023-06-20', daysOverdue: 0,
    title: 'Cleaning Validation Gap Identified in CDSCO Inspection',
    description: 'CDSCO inspection (AUD-2023-002) raised a critical observation: cleaning validation for shared equipment used in Amoxicillin and Metformin manufacturing was not current. Swab recovery data missing for new equipment configuration.',
    rootCause: 'Why 1: Cleaning validation data absent\nWhy 2: Equipment configuration change not linked to revalidation trigger\nWhy 3: Change control SOP did not list cleaning revalidation as mandatory step\nWhy 4: SOP review cycle was overdue by 8 months\nWhy 5: SOP owner transfer left a gap in accountability',
    actionPlan: '1. Immediately suspend shared equipment use pending revalidation\n2. Conduct full cleaning validation with TOC and HPLC swab recovery\n3. Revise change control SOP to include cleaning revalidation gate\n4. Assign SOP owners in HRMS with escalation alerts\n5. Complete revalidation study and submit response to CDSCO',
    createdAt: '2023-04-25T09:30:00Z',
  },
  {
    id: 'mock-capa-2302', capaCode: 'CAPA-2023-002', source: 'OOS Result',
    product: 'Ibuprofen 400mg Tablets', batchNo: 'BT-IBU-2304',
    severity: 'major', status: 'closed', department: 'Quality Control',
    owner: { name: 'Ramesh Kulkarni' }, dueDate: '2023-08-30', daysOverdue: 0,
    title: 'OOS Dissolution Result — Phase II Investigation Required',
    description: 'Ibuprofen 400mg batch BT-IBU-2304 failed dissolution at 45-minute timepoint (Q = 70%, limit NLT 80%). Phase I investigation ruled out analyst error and instrument failure. Phase II investigation initiated.',
    rootCause: 'Why 1: Dissolution OOS at 45 min\nWhy 2: Drug release retarded by excess polymer coating\nWhy 3: Polymer dispersion concentration higher than specification\nWhy 4: Preparation SOP allowed a ±5% range but analyst prepared at +4.8% without documentation\nWhy 5: No IPC check at dispersion preparation step',
    actionPlan: '1. Initiate batch failure investigation and quarantine\n2. Tighten polymer concentration SOP to ±2%\n3. Add gravimetric verification IPC step for coating dispersion\n4. Retrain coating team on SOP\n5. Perform process capability study on coating step',
    createdAt: '2023-07-10T10:00:00Z',
  },
  {
    id: 'mock-capa-2303', capaCode: 'CAPA-2023-003', source: 'Equipment Failure',
    product: 'Sodium Chloride 0.9% Injection', batchNo: 'BT-SAL-2305',
    severity: 'minor', status: 'closed', department: 'Engineering',
    owner: { name: 'Arun Menon' }, dueDate: '2023-10-15', daysOverdue: 0,
    title: 'Autoclave Temperature Sensor Drift — Sterility Assurance Review',
    description: 'During routine calibration, the autoclave temperature sensor was found to be drifting +0.4°C beyond tolerance. All batches processed in the prior 3-month window require retrospective review.',
    rootCause: 'Sensor drift due to scale build-up on thermocouple probe. Preventive maintenance schedule did not include probe descaling. Maintenance was last performed 14 months ago against a 6-month SOP requirement.',
    actionPlan: '1. Recalibrate all thermocouples in the autoclave\n2. Perform retrospective risk assessment on batches processed during drift period\n3. Update PM schedule to 6-month frequency with descaling step\n4. Add calibration status check to batch release checklist',
    createdAt: '2023-09-04T08:15:00Z',
  },
  {
    id: 'mock-capa-2304', capaCode: 'CAPA-2023-004', source: 'Self-inspection',
    product: 'Atorvastatin 10mg Tablets', batchNo: 'BT-ATV-2306',
    severity: 'major', status: 'closed', department: 'Manufacturing',
    owner: { name: 'Suresh Patil' }, dueDate: '2023-12-31', daysOverdue: 0,
    title: 'Rejected Batch Investigation — Binder Over-granulation Root Cause',
    description: 'Batch BT-ATV-2306 rejected due to dissolution failure. Self-inspection following rejection identified binder over-granulation as the root cause. Process parameters during high-shear granulation exceeded validated ranges.',
    rootCause: 'Fishbone Analysis:\nPeople: Operator error — granulation endpoint not verified by analyst on shift\nProcess: Endpoint parameter (impeller torque) was monitored but action limit not enforced\nEquipment: High-shear mixer display calibration was out of tolerance by +7%\nMaterials: Binder lot had higher viscosity than specification average\nMeasurement: Torque trending data not reviewed in real time',
    actionPlan: '1. Recalibrate high-shear mixer torque display\n2. Revise process instructions to require analyst sign-off at granulation endpoint\n3. Implement real-time torque trend review during granulation\n4. Qualify new binder viscosity acceptance range\n5. Conduct process FMEA for granulation step',
    createdAt: '2023-10-12T14:00:00Z',
  },
  // 2024
  {
    id: 'mock-capa-2401', capaCode: 'CAPA-2024-001', source: 'Audit Finding',
    product: 'Paracetamol 650mg ER Tablets', batchNo: 'BT-PCM-2302',
    severity: 'critical', status: 'closed', department: 'Quality Assurance',
    owner: { name: 'Dr. Preeti Joshi' }, dueDate: '2024-04-22', daysOverdue: 0,
    title: 'OOS Investigation Records Incomplete — CDSCO Follow-up Audit Finding',
    description: 'CDSCO follow-up audit (AUD-2024-001) identified that OOS investigation reports for three batches lacked Phase II investigation documentation. Traceability of reviewed original data missing in two cases.',
    rootCause: 'Why 1: OOS investigation report incomplete\nWhy 2: Phase II investigation closed prematurely\nWhy 3: SOP-QC-OOS-004 did not specify minimum documentation requirements for Phase II\nWhy 4: SOP was last revised in 2020 and predates current CDSCO guidance on OOS\nWhy 5: Annual SOP review process did not capture regulatory guidance updates',
    actionPlan: '1. Revise SOP-QC-OOS-004 to align with CDSCO 2023 OOS guidance\n2. Retrospectively complete Phase II documentation for three affected batches\n3. Train all QC analysts on updated OOS SOP\n4. Include regulatory guidance review in annual SOP update process\n5. Create OOS tracker with mandatory Phase II completion gate in LIMS',
    createdAt: '2024-01-30T09:00:00Z',
  },
  {
    id: 'mock-capa-2402', capaCode: 'CAPA-2024-002', source: 'Deviation',
    product: 'Metformin 500mg Tablets', batchNo: 'BT-MET-2406',
    severity: 'minor', status: 'closed', department: 'Manufacturing',
    owner: { name: 'Deepak Rao' }, dueDate: '2024-10-31', daysOverdue: 0,
    title: 'Blend Uniformity Deviation During Lubrication Step',
    description: 'During IPC testing for BT-MET-2406, blend uniformity RSD exceeded specification (4.1% vs limit NMT 3.0%) at the lubrication endpoint. Batch processing paused pending investigation.',
    rootCause: 'Why 1: Blend RSD exceeded limit at lubrication endpoint\nWhy 2: Over-lubrication caused hydrophobic barrier on tablet surface\nWhy 3: Lubricant mixing time exceeded validated parameter by 3 minutes\nWhy 4: Operator re-started the blender after a breakdown without adjusting timer\nWhy 5: Equipment restart SOP did not address timer reset protocol after interruption',
    actionPlan: '1. Update blender restart SOP to require timer reset and supervisor approval\n2. Add lubrication endpoint IPC check with blender interlock\n3. Retrain blending operators on restart protocol\n4. Review all similar equipment SOPs for restart gap',
    createdAt: '2024-09-10T11:30:00Z',
  },
  {
    id: 'mock-capa-2403', capaCode: 'CAPA-2024-003', source: 'Customer Complaint',
    product: 'Azithromycin 500mg Tablets', batchNo: 'BT-AZI-2403',
    severity: 'major', status: 'closed', department: 'Quality Control',
    owner: { name: 'Kavitha Nair' }, dueDate: '2024-07-15', daysOverdue: 0,
    title: 'Customer Complaint — Film Coat Peeling in Tropical Climate Storage',
    description: 'Distributor in Southeast Asia reported film coating peeling in 0.8% of tablets in a shipped lot of Azithromycin 500mg. Product stored at local temperature (32-38°C) with humidity spikes. Complaint trend identified across two batches.',
    rootCause: 'Fishbone:\nMaterials: HPMC film former lot with lower viscosity grade than specification average\nProcess: Coating inlet air temperature at upper validated limit (45°C) compounded moisture uptake\nEnvironment: Finished product not validated for zone IVb climate storage\nPeople: Stability protocol did not include zone IVb condition study for this product',
    actionPlan: '1. Initiate field corrective action — customer notification and replacement\n2. Conduct zone IVb stability study (40°C/75% RH) for Azithromycin\n3. Review HPMC lot release specification to tighten viscosity range\n4. Revise coating SOP to lower inlet air temperature for tropical market batches\n5. Label artwork update with storage warning for high humidity regions',
    createdAt: '2024-05-20T13:00:00Z',
  },
  {
    id: 'mock-capa-2404', capaCode: 'CAPA-2024-004', source: 'Regulatory Inspection',
    product: 'Sodium Chloride 0.9% Injection', batchNo: 'BT-SAL-2405',
    severity: 'critical', status: 'closed', department: 'Manufacturing',
    owner: { name: 'Priya Nair' }, dueDate: '2024-09-09', daysOverdue: 0,
    title: 'Lyophilizer Cycle Validation Documentation Gap — WHO Inspection Finding',
    description: 'WHO GMP inspection (AUD-2024-004) identified one critical finding: lyophilizer cycle validation report for the sterile injectable line did not include a demonstration run at the worst-case load configuration. Three commercial batches processed under unvalidated conditions.',
    rootCause: 'Why 1: Cycle validation report incomplete\nWhy 2: Worst-case load study was deferred during qualification due to equipment delivery delays\nWhy 3: Deferral was risk-accepted internally but not documented in the validation plan\nWhy 4: Validation plan did not have a mandatory completion checklist before commercial use approval\nWhy 5: QA release process for equipment qualification did not require validation plan closure confirmation',
    actionPlan: '1. Immediately place all 3 affected batches on quarantine pending risk assessment\n2. Conduct worst-case load lyophilizer cycle validation study\n3. Submit completed validation report to WHO\n4. Revise equipment qualification SOP to mandate validation plan closure before commercial use\n5. Audit all other equipment validation reports for similar gaps',
    createdAt: '2024-07-15T10:30:00Z',
  },
  {
    id: 'mock-capa-2405', capaCode: 'CAPA-2024-005', source: 'Self-inspection',
    product: 'Pantoprazole 40mg Tablets', batchNo: 'BT-PAN-2404',
    severity: 'minor', status: 'closed', department: 'Quality Control',
    owner: { name: 'Meena Iyer' }, dueDate: '2024-12-15', daysOverdue: 0,
    title: 'Reference Standard Management Lacks Traceability — Self-Inspection Finding',
    description: 'During self-inspection of QC laboratory, reference standard receipt, use, and disposal records were found to be maintained in multiple non-linked logs. Chain of custody for two Pantoprazole working standards could not be reconstructed.',
    rootCause: 'Reference standard management SOP predates introduction of LIMS. Manual logs were used but not integrated. LIMS module for reference standards was available but not implemented due to resource constraints.',
    actionPlan: '1. Activate LIMS reference standard management module\n2. Migrate existing reference standard records into LIMS\n3. Retire paper-based logs and mandate LIMS usage\n4. Train QC analysts on new workflow\n5. Include reference standard traceability in monthly QC audit checklist',
    createdAt: '2024-10-28T09:00:00Z',
  },
  {
    id: 'mock-capa-2406', capaCode: 'CAPA-2024-006', source: 'Deviation',
    product: 'Ibuprofen 400mg Tablets', batchNo: 'BT-IBU-2407',
    severity: 'minor', status: 'closed', department: 'Manufacturing',
    owner: { name: 'Vijay Sharma' }, dueDate: '2024-12-31', daysOverdue: 0,
    title: 'Tablet Weight Variation Deviation — Compression Process Optimisation',
    description: 'Batch BT-IBU-2407 exhibited tablet weight RSD of 2.8% (limit NMT 2.0%) during IPC checks at the compression stage. Batch released after extended IPC monitoring confirmed no out-of-specification tablets in final product.',
    rootCause: 'Weight variation caused by inconsistent powder flow from hopper. Pre-blend particle size distribution had higher fines content than historical average due to a new milling equipment setting.',
    actionPlan: '1. Revert milling parameter to validated setting\n2. Add particle size distribution (D90) as IPC check post-milling\n3. Tighten compression IPC check frequency to every 15 minutes for first 30 minutes\n4. Conduct blend flow characterisation study',
    createdAt: '2024-10-20T14:15:00Z',
  },
  // 2025
  {
    id: 'mock-capa-2501', capaCode: 'CAPA-2025-001', source: 'OOS Result',
    product: 'Amoxicillin 500mg Capsules', batchNo: 'BT-AMX-2501',
    severity: 'major', status: 'in_progress', department: 'Quality Control',
    owner: { name: 'Dr. Anjali Mehta' }, dueDate: '2025-05-15', daysOverdue: 0,
    title: 'OOS Result — Dissolution Failure in Amoxicillin Batch',
    description: 'Dissolution test for Amoxicillin 500mg batch BT-AMX-2501 failed specification at 30-min timepoint (Q = 62%, limit NLT 75%). Phase I investigation completed; analyst error and instrument failure excluded. Phase II initiated.',
    rootCause: 'Why 1: Dissolution failure\nWhy 2: Inadequate disintegrant level in capsule fill\nWhy 3: Raw material moisture content exceeded IPC limit\nWhy 4: Humidity excursion in warehouse section W-3\nWhy 5: HVAC unit maintenance for W-3 was overdue by 6 weeks',
    actionPlan: '1. Quarantine affected batch\n2. Retesting with fresh samples from reserve\n3. Inspect HVAC maintenance records for W-3\n4. Recalibrate humidity sensors in warehouse\n5. Conduct moisture mapping study for storage zone\n6. Revise HVAC PM schedule to auto-escalate overdue tasks',
    createdAt: '2025-03-20T09:00:00Z',
  },
  {
    id: 'mock-capa-2502', capaCode: 'CAPA-2025-002', source: 'Audit Finding',
    product: 'Ciprofloxacin 250mg Tablets', batchNo: 'BT-CIP-2502',
    severity: 'critical', status: 'open', department: 'Manufacturing',
    owner: { name: 'Ramesh Kulkarni' }, dueDate: '2025-04-30', daysOverdue: 0,
    title: 'Critical GDP Gap — Incomplete Batch Records',
    description: 'Internal audit identified incomplete manufacturing entries in BMR for Cipro 250mg batch. Missing operator sign-offs on 3 unit operations. Incomplete in-process control data for mixing and compression stages.',
    rootCause: 'Fishbone: People — Inadequate training on GDP requirements; Process — No real-time review checkpoint during shift; Equipment — Paper-based BMR system prone to omissions; Measurement — No automated completeness check before batch handover.',
    actionPlan: '1. Re-train all manufacturing operators on GDP within 2 weeks\n2. Implement electronic batch record system with mandatory field completion\n3. Introduce supervisor checkpoint sign-off at each unit operation handover\n4. Add BMR completeness review to batch release checklist',
    createdAt: '2025-03-25T11:30:00Z',
  },
  {
    id: 'mock-capa-2503', capaCode: 'CAPA-2025-003', source: 'Equipment Failure',
    product: 'Sodium Chloride 0.9% Injection', batchNo: 'BT-SAL-2503',
    severity: 'minor', status: 'closed', department: 'Quality Assurance',
    owner: { name: 'Priya Nair' }, dueDate: '2025-03-31', daysOverdue: 0,
    title: 'HPLC Column Failure — Method Revalidation Required',
    description: 'Column efficiency dropped below system suitability limits (theoretical plates < 2000). Peak tailing factor exceeded specification (Tf = 2.3 vs limit NMT 2.0). Column age: 19 months.',
    rootCause: 'Column end-of-life reached; no defined replacement schedule in equipment SOP. Column last replaced 19 months ago vs. recommended 12-month lifecycle. Replacement not triggered due to absence of usage tracking.',
    actionPlan: '1. Replace column immediately and verify full SST\n2. Update equipment SOP to include column replacement at 12-month or 1000-injection limit\n3. Add column usage log to LIMS equipment module\n4. Create auto-alert for upcoming column replacement in maintenance scheduler',
    createdAt: '2025-02-14T08:00:00Z',
  },
  {
    id: 'mock-capa-2504', capaCode: 'CAPA-2025-004', source: 'Customer Complaint',
    product: 'Paracetamol 650mg ER Tablets', batchNo: 'BT-PCM-2504',
    severity: 'major', status: 'overdue', department: 'Quality Control',
    owner: { name: 'Vijay Sharma' }, dueDate: '2025-03-01', daysOverdue: 42,
    title: 'Customer Complaint — Tablet Colour Variation',
    description: 'Three customer complaints received about visible colour variation within the same blister pack of Paracetamol 650mg ER Tablets. Affected lot: BT-PCM-2504. Colour non-uniformity visible to naked eye.',
    rootCause: 'Pigment blend segregation during coating process due to high pan speed. Coating process parameters exceeded upper validated limit (pan speed 18 rpm vs. validated 12-15 rpm). Process parameter deviation not flagged during batch.',
    actionPlan: '1. Review and tighten coating pan speed validated range to 12-14 rpm\n2. Re-qualify coating process with updated parameters\n3. Implement IPC colour check at 15% coating weight gain intervals\n4. Add pan speed out-of-range alarm to coating system\n5. Update market complaint trending to detect cosmetic defect patterns',
    createdAt: '2025-01-28T14:00:00Z',
    escalationLevel: 2,
  },
  {
    id: 'mock-capa-2505', capaCode: 'CAPA-2025-005', source: 'Regulatory Inspection',
    product: 'Sodium Chloride 0.9% Injection', batchNo: 'BT-SAL-2503',
    severity: 'critical', status: 'in_progress', department: 'Manufacturing',
    owner: { name: 'Arun Menon' }, dueDate: '2025-06-30', daysOverdue: 0,
    title: 'Grade B Environmental Exceedance Not Escalated Within SOP Timeframe',
    description: 'GMP surveillance audit (AUD-2025-005) raised a critical finding: a Grade B viable particle exceedance from 3 months prior was not escalated to QA Head within the 24-hour requirement per SOP-ENV-007. Investigation and CAPA report submitted 9 days late.',
    rootCause: 'Why 1: Exceedance not escalated within SOP timeframe\nWhy 2: Shift supervisor not aware of 24-hour escalation requirement\nWhy 3: SOP-ENV-007 escalation clause was updated but training matrix not refreshed\nWhy 4: Change control for SOP update did not trigger mandatory re-training\nWhy 5: Training impact assessment in change control SOP was not enforced',
    actionPlan: '1. Immediate re-training of all Grade B area personnel on SOP-ENV-007\n2. Revise change control SOP to mandate training impact assessment sign-off\n3. Implement automated LIMS alert for environmental exceedances requiring escalation\n4. Conduct monthly drill for environmental exceedance escalation protocol\n5. Add escalation compliance metric to monthly quality review',
    createdAt: '2025-08-25T10:00:00Z',
  },
  {
    id: 'mock-capa-2506', capaCode: 'CAPA-2025-006', source: 'Self-inspection',
    product: 'Prednisolone 5mg Tablets', batchNo: 'BT-PRD-2507',
    severity: 'minor', status: 'in_progress', department: 'Quality Control',
    owner: { name: 'Meena Iyer' }, dueDate: '2025-09-30', daysOverdue: 0,
    title: 'Inter-laboratory Comparison Data Not Documented for Accreditation',
    description: 'ISO 17025 self-inspection identified that inter-laboratory comparison (ILC) participation data for 2024 was not formally documented in the quality record system. Bureau Veritas accreditation audit risk raised.',
    rootCause: 'ILC results were communicated via email and filed in personal drives. No formal procedure for ILC data capture and LIMS integration. Accreditation SOP did not specify data custodian for ILC records.',
    actionPlan: '1. Retroactively compile and archive all 2024 ILC data\n2. Update accreditation SOP to specify ILC data submission to LIMS within 5 working days\n3. Assign ILC data custodian role to QC Manager\n4. Add ILC record review to quarterly ISO 17025 internal audit checklist',
    createdAt: '2025-07-10T09:30:00Z',
  },
  // 2026
  {
    id: 'mock-capa-2601', capaCode: 'CAPA-2026-001', source: 'OOS Result',
    product: 'Metformin 500mg Tablets', batchNo: 'BT-MET-2601',
    severity: 'major', status: 'open', department: 'Quality Control',
    owner: { name: 'Deepak Rao' }, dueDate: '2026-03-15', daysOverdue: 0,
    title: 'OOS Assay Result — Metformin Content Below Specification',
    description: 'Assay for BT-MET-2601 returned 97.2% (specification 98.0-102.0%). Phase I investigation in progress. Analyst confirmed correct procedure followed; instrument SST passed. Reserve samples retained for Phase II.',
    rootCause: 'Phase I investigation ongoing. Preliminary data suggests content uniformity issue in lower tablet layers of the compression run. Root cause analysis pending blend sample results.',
    actionPlan: '1. Initiate Phase I investigation per SOP-QC-OOS-004\n2. Test reserve samples from multiple compression intervals\n3. Conduct Phase II investigation if Phase I inconclusive\n4. Retain batch on quarantine pending investigation outcome',
    createdAt: '2026-01-20T11:00:00Z',
  },
  {
    id: 'mock-capa-2602', capaCode: 'CAPA-2026-002', source: 'Audit Finding',
    product: 'Ibuprofen 400mg Tablets', batchNo: 'BT-IBU-2602',
    severity: 'minor', status: 'open', department: 'Quality Assurance',
    owner: { name: 'Dr. Preeti Joshi' }, dueDate: '2026-04-30', daysOverdue: 0,
    title: 'SOP Revision Schedule Backlog — Internal Audit Observation',
    description: 'Annual internal audit (AUD-2026-002) identified 7 SOPs with overdue periodic review dates. Two SOPs are more than 12 months past their review due date. No dispensation recorded in document management system.',
    rootCause: 'SOP ownership transfers following personnel changes were not reflected in the document management system. Automated review reminders were sent to former owners who had left the department.',
    actionPlan: '1. Complete SOP owner audit and update all assignments within 2 weeks\n2. Review and approve all 7 overdue SOPs within 30 days\n3. Reconfigure document management system to send reminders to department head if no owner assigned\n4. Add SOP review compliance to monthly QA metrics dashboard',
    createdAt: '2026-03-12T09:00:00Z',
  },
  {
    id: 'mock-capa-2603', capaCode: 'CAPA-2026-003', source: 'Deviation',
    product: 'Atorvastatin 10mg Tablets', batchNo: 'BT-ATV-2603',
    severity: 'minor', status: 'open', department: 'Manufacturing',
    owner: { name: 'Sonal Desai' }, dueDate: '2026-05-15', daysOverdue: 0,
    title: 'Packaging Line Speed Deviation — Label Application Integrity Concern',
    description: 'During packaging of BT-ATV-2603, the blister line speed exceeded the validated range by 8% for approximately 20 minutes before operator intervention. Labels on 1,200 blisters may have incomplete adhesion.',
    rootCause: 'Speed controller upper limit was reset after an engineer performed a belt tension adjustment. Post-adjustment verification of speed setpoint was not completed per the equipment SOP.',
    actionPlan: '1. Quarantine and 100% inspect the 1,200 blisters processed during the exceedance window\n2. Update equipment SOP to mandate parameter verification after any mechanical adjustment\n3. Add speed setpoint lock-out function to line configuration\n4. Retrain packaging operators and engineers on post-maintenance verification',
    createdAt: '2026-03-10T15:00:00Z',
  },
  {
    id: 'mock-capa-2604', capaCode: 'CAPA-2026-004', source: 'Customer Complaint',
    product: 'Azithromycin 500mg Tablets', batchNo: 'BT-AZI-2505',
    severity: 'major', status: 'overdue', department: 'Quality Control',
    owner: { name: 'Harish Reddy' }, dueDate: '2026-02-28', daysOverdue: 15,
    title: 'Complaint — Unusual Tablet Odour Reported by Hospital Pharmacy',
    description: 'Hospital pharmacy in Hyderabad reported unusual odour from Azithromycin 500mg tablets. Two independent reports from same lot (BT-AZI-2505). Investigation required to rule out degradation, packaging interaction, or raw material issue.',
    rootCause: 'Root cause investigation in progress. Preliminary GC headspace analysis of retain samples detected trace levels of acetic acid-related volatile, consistent with excipient hydrolysis pathway. Full analysis pending.',
    actionPlan: '1. Recall risk assessment in progress\n2. GC headspace analysis of retain and complaint samples\n3. Evaluate excipient sourcing — acetic acid-related impurity profile\n4. Escalate to QA Director if degradation confirmed\n5. Regulatory notification assessment based on investigation outcome',
    createdAt: '2026-01-30T10:00:00Z',
    escalationLevel: 1,
  },
];

export default function Capa() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [severityFilter, setSeverityFilter] = useState(() => searchParams.get('severity') ?? '');
  const [labId, setLabId] = useState('');
  const [dateRange, setDateRange] = useState('6m');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCapa, setSelectedCapa] = useState<Capa | null>(null);
  const [form, setForm] = useState({ title: '', source: '', product: '', severity: 'major', description: '', dueDate: '', department: '', rootCause: '' });
  const [rootCauseTemplate, setRootCauseTemplate] = useState('');
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [effectivenessDate, setEffectivenessDate] = useState('');
  const [effectivenessNotes, setEffectivenessNotes] = useState('');
  const queryClient = useQueryClient();

  const exportColumns = [
    { key: 'capaCode', label: 'CAPA ID' },
    { key: 'title', label: 'Title' },
    { key: 'severity', label: 'Severity' },
    { key: 'status', label: 'Status' },
    { key: 'department', label: 'Department' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'daysOverdue', label: 'Days Overdue' },
  ];
  const crumbs = useBreadcrumb();

  const { data, isLoading } = useQuery({
    queryKey: ['capass', { search, status: statusFilter, severity: severityFilter, page, labId, dateRange }],
    queryFn: () =>
      api.get('/capa', { params: { search, status: statusFilter, severity: severityFilter, page, limit: 20, ...(labId && { labId }), range: dateRange } })
        .then((r) => r.data),
  });

  // Auto-open detail when navigated from a notification (?openId=capaN)
  const openId = searchParams.get('openId');
  const { data: openCapaData } = useQuery({
    queryKey: ['capa-detail', openId],
    queryFn: () => api.get(`/capa/${openId}`).then(r => r.data.data as Capa),
    enabled: !!openId,
  });
  useEffect(() => {
    if (openCapaData) setSelectedCapa(openCapaData);
  }, [openCapaData]);

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['capa-trend', labId, dateRange],
    queryFn: () => api.get('/capa/monthly-trend', { params: { ...(labId && { labId }), range: dateRange } }).then((r) => r.data.data),
  });

  const { data: severityData } = useQuery({
    queryKey: ['capa-severity', labId, dateRange],
    queryFn: () => api.get('/capa/by-severity', { params: { ...(labId && { labId }), range: dateRange } }).then((r) => r.data.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/capa', { ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capass'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowCreate(false);
      setForm({ title: '', source: '', product: '', severity: 'major', description: '', dueDate: '', department: '', rootCause: '' });
      setRootCauseTemplate('');
    },
  });

  const escalateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/capa/${id}/escalate`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capass'] });
      setShowEscalateForm(false);
      setEscalateReason('');
    },
  });

  const effectivenessMutation = useMutation({
    mutationFn: ({ id, scheduledDate, notes }: { id: string; scheduledDate: string; notes: string }) =>
      api.post(`/capa/${id}/effectiveness-check`, { scheduledDate, notes }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capass'] }),
  });

  const handleTemplateChange = (template: string) => {
    setRootCauseTemplate(template);
    const templates: Record<string, string> = {
      '5whys':   'Why 1:\nWhy 2:\nWhy 3:\nWhy 4:\nWhy 5:',
      fishbone:  'People:\nProcess:\nEquipment:\nEnvironment:\nMaterials:\nMeasurement:',
      fta:       'Top Event:\n  Fault 1:\n    Cause A:\n    Cause B:\n  Fault 2:\n    Cause C:',
      fmea:      'Failure Mode:\nEffect:\nCause:\nSeverity (1-10):\nOccurrence (1-10):\nDetection (1-10):\nRPN:',
      freetext:  '',
    };
    if (template in templates) setForm((f) => ({ ...f, rootCause: templates[template] }));
  };

  const { exportCSV, exportPDF } = useTableExport(data?.data ?? [], exportColumns, 'capa-list');

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'capaCode', label: 'ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.capaCode)}</span> },
    { key: 'title', label: 'Title',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm truncate max-w-xs">{String(row.title)}</p>
          <p className="text-xs text-text-dim">{String(row.source)} · {String(row.product || '—')}</p>
        </div>
      )},
    { key: 'severity', label: 'Severity',
      render: (row) => <PillBadge value={String(row.severity)} /> },
    { key: 'status', label: 'Status',
      render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'owner', label: 'Owner',
      render: (row) => {
        const owner = row.owner as { name: string } | null;
        return <span className="text-xs text-text-muted">{owner?.name || '—'}</span>;
      }},
    { key: 'dueDate', label: 'Due Date',
      render: (row) => {
        const daysOverdue = Number(row.daysOverdue);
        const daysUntilDue = differenceInDays(new Date(String(row.dueDate)), new Date());
        const escalationLevel = Number(row.escalationLevel ?? 1);
        return (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-mono">{format(new Date(String(row.dueDate)), 'd MMM yyyy')}</p>
            {daysOverdue > 14 && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#7F1D1D', color:'#fff', display:'inline-block' }}>ESCALATED</span>
            )}
            {daysOverdue > 0 && daysOverdue <= 14 && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#FEE2E2', color:'#DC2626', display:'inline-block' }}>{daysOverdue}d OVERDUE</span>
            )}
            {daysOverdue === 0 && daysUntilDue <= 7 && daysUntilDue >= 0 && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#FEF3C7', color:'#D97706', display:'inline-block' }}>DUE SOON</span>
            )}
            {escalationLevel > 1 && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#FFF7ED', color:'#EA580C', display:'inline-block' }}>L{escalationLevel}</span>
            )}
          </div>
        );
      }},
  ];

  const handleExport = () => {
    const rows = data?.data as Capa[] || [];
    const csv = ['ID,Title,Source,Severity,Status,Owner,Due Date'].concat(
      rows.map((c) => `${c.capaCode},"${c.title}",${c.source},${c.severity},${c.status},${c.owner?.name || ''},${c.dueDate}`)
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'capa-list.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      <DrillBreadcrumb crumbs={crumbs} />
      <PageHeader
        title="CAPA / Deviations"
        subtitle={`${data?.meta?.total || 0} total records · Active quality events`}
        actions={
          <>
            <ALCOABadge />
            <button onClick={exportCSV} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#F0FDF4', border:'1px solid #16A34A30', color:'#16A34A', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <Download size={13} /> CSV
            </button>
            <button onClick={exportPDF} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#EFF6FF', border:'1px solid #2563EB30', color:'#2563EB', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <FileText size={13} /> PDF
            </button>
            <CanAccess roles={['admin', 'qa_director', 'lab_head', 'qc_analyst']}>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> New CAPA</button>
            </CanAccess>
          </>
        }
      />

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        dateRange={dateRange} onDateRangeChange={(r) => { setDateRange(r); setPage(1); }}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {trendLoading ? <ChartSkeleton height={200} /> : (
            <ChartCard title="12-Month CAPA Trend" subtitle="Open / Closed / Overdue" height={200}>
              <ResponsiveContainer>
                <AreaChart data={trendData || []} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <defs>
                    <linearGradient id="gOv2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC2626" stopOpacity={0.15} /><stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gCv2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16A34A" stopOpacity={0.15} /><stop offset="95%" stopColor="#16A34A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6899A0' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={25} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="open" name="Open" stroke="#DC2626" fill="url(#gOv2)" strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="closed" name="Closed" stroke="#16A34A" fill="url(#gCv2)" strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="overdue" name="Overdue" stroke="#D97706" fill="none" strokeDasharray="4 2" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
        <ChartCard title="By Severity" subtitle="Open CAPAs breakdown" height={200}>
          <ResponsiveContainer>
            <BarChart data={severityData || []} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }} barSize={14}>
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="severity" tick={{ fontSize: 10 }} width={70} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" name="Count" radius={[0, 2, 2, 0]}>
                {(severityData || []).map((entry: { color: string }, i: number) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search CAPAs..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="overdue">Overdue</option>
          <option value="closed">Closed</option>
        </select>
        <select value={severityFilter} onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Severity</option>
          <option value="critical">Critical</option>
          <option value="major">Major</option>
          <option value="minor">Minor</option>
          <option value="observation">Observation</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={((data?.data && data.data.length > 0) ? data.data : MOCK_CAPAS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedCapa(row as unknown as Capa)}
        total={data?.meta?.total}
        page={page}
        onPageChange={setPage}
      />

      {/* Create CAPA Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create New CAPA"
        subtitle="Corrective & Preventive Action record" size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create CAPA'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="CAPA title..." />
          </div>
          <div>
            <label className="label">Source</label>
            <select className="select" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
              <option value="">Select source...</option>
              {['Manufacturing', 'QC Testing', 'Audit Finding', 'Customer Complaint', 'OOS Result', 'Equipment Failure'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Severity *</label>
            <select className="select" value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
              <option value="observation">Observation</option>
              <option value="minor">Minor</option>
              <option value="major">Major</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="label">Product / Batch</label>
            <input className="input" value={form.product} onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))} placeholder="Product name..." />
          </div>
          <div>
            <label className="label">Due Date *</label>
            <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Description *</label>
            <textarea className="input resize-none" rows={3} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe the deviation..." />
          </div>
          <div>
            <label className="label">Root Cause Method</label>
            <select className="select" value={rootCauseTemplate} onChange={(e) => handleTemplateChange(e.target.value)}>
              <option value="">Select template...</option>
              <option value="5whys">5 Whys Analysis</option>
              <option value="fishbone">Fishbone / Ishikawa</option>
              <option value="fta">Fault Tree Analysis</option>
              <option value="fmea">Failure Mode & Effects Analysis</option>
              <option value="freetext">Free text</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Root Cause</label>
            <textarea className="input resize-none font-mono text-xs" rows={4} value={form.rootCause}
              onChange={(e) => setForm((f) => ({ ...f, rootCause: e.target.value }))} placeholder="Enter root cause analysis..." />
          </div>
          {/* ALCOA+ Acknowledgment */}
          <div className="col-span-2 p-3 rounded-lg" style={{ background: '#F0FDF4', border: '1px solid rgba(22,163,74,0.2)' }}>
            <div className="flex items-start gap-2">
              <input type="checkbox" id="alcoa-confirm" required className="mt-0.5 accent-green-600" />
              <label htmlFor="alcoa-confirm" className="text-xs text-green-800 leading-relaxed cursor-pointer">
                <span className="font-semibold">ALCOA+ Declaration:</span> I confirm that the information provided is <strong>Attributable</strong> to me, <strong>Legible</strong>, recorded <strong>Contemporaneously</strong>, <strong>Original</strong>, and <strong>Accurate</strong> to the best of my knowledge. (21 CFR Part 11 / EU Annex 11)
              </label>
            </div>
          </div>
        </div>
      </Modal>

      {/* CAPA Detail Modal */}
      <Modal isOpen={!!selectedCapa} onClose={() => setSelectedCapa(null)} title={selectedCapa?.capaCode || ''}
        subtitle={selectedCapa?.title} size="lg">
        {selectedCapa && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Severity</p><PillBadge value={selectedCapa.severity} /></div>
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Status</p><PillBadge value={selectedCapa.status} showDot /></div>
              <div className="p-3 bg-bg rounded-lg"><p className="text-xs text-text-muted">Due</p><p className="text-sm font-mono mt-0.5">{format(new Date(selectedCapa.dueDate), 'd MMM yyyy')}</p></div>
            </div>
            <div>
              <p className="text-xs font-semibold text-text-muted mb-1">Description</p>
              <p className="text-sm text-text leading-relaxed">{selectedCapa.description}</p>
            </div>
            {selectedCapa.rootCause && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Root Cause</p>
                <p className="text-sm text-text leading-relaxed">{selectedCapa.rootCause}</p>
              </div>
            )}
            {selectedCapa.actionPlan && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Action Plan</p>
                <p className="text-sm text-text leading-relaxed">{selectedCapa.actionPlan}</p>
              </div>
            )}
            {/* Escalation section */}
            <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:10, padding:'12px 14px' }}>
              <p className="text-xs font-semibold text-text-muted mb-2">Escalation Path</p>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
                {[{ l:1, label:'Owner' },{ l:2, label:'Dept Mgr' },{ l:3, label:'QA Head' },{ l:4, label:'Executive' }].map(({ l, label }, idx) => {
                  const active = (selectedCapa.escalationLevel ?? 1) >= l;
                  return (
                    <React.Fragment key={l}>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, background: active ? '#EA580C' : 'rgba(0,0,0,0.1)', color: active ? '#fff' : '#94A3B8', margin:'0 auto 2px' }}>L{l}</div>
                        <span style={{ fontSize:9, color: active ? '#EA580C' : '#94A3B8', fontWeight: active ? 700 : 400 }}>{label}</span>
                      </div>
                      {idx < 3 && <div style={{ flex:1, height:2, background: active && (selectedCapa.escalationLevel ?? 1) > l ? '#EA580C' : 'rgba(0,0,0,0.1)', borderRadius:1 }} />}
                    </React.Fragment>
                  );
                })}
              </div>
              {!showEscalateForm ? (
                <button onClick={() => setShowEscalateForm(true)} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'#EA580C', color:'#fff', border:'none', cursor:'pointer', fontWeight:600 }}>
                  Escalate to Next Level
                </button>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <textarea rows={2} placeholder="Reason for escalation..." value={escalateReason} onChange={(e) => setEscalateReason(e.target.value)} style={{ fontSize:12, padding:'6px 8px', borderRadius:6, border:'1px solid #FED7AA', resize:'none', outline:'none' }} />
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => escalateMutation.mutate({ id: selectedCapa.id, reason: escalateReason })} disabled={!escalateReason.trim() || escalateMutation.isPending} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'#EA580C', color:'#fff', border:'none', cursor:'pointer', fontWeight:600 }}>
                      {escalateMutation.isPending ? 'Escalating...' : 'Confirm'}
                    </button>
                    <button onClick={() => { setShowEscalateForm(false); setEscalateReason(''); }} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'none', border:'1px solid rgba(0,0,0,0.1)', cursor:'pointer', color:'#5a5a6e' }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* Effectiveness Check */}
            <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 14px' }}>
              <p className="text-xs font-semibold text-text-muted mb-2">Effectiveness Check</p>
              {selectedCapa.effectivenessCheck?.scheduledDate ? (
                <p style={{ fontSize:12, color:'#16A34A', marginBottom:8 }}>Scheduled: {format(new Date(selectedCapa.effectivenessCheck.scheduledDate), 'd MMM yyyy')} · Status: <strong>{selectedCapa.effectivenessCheck.status ?? 'pending'}</strong></p>
              ) : null}
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <div style={{ flex:1 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:'#5a5a6e', display:'block', marginBottom:3 }}>Scheduled Check Date</label>
                  <input type="date" value={effectivenessDate} onChange={(e) => setEffectivenessDate(e.target.value)} style={{ fontSize:12, padding:'5px 8px', borderRadius:6, border:'1px solid #BBF7D0', outline:'none', width:'100%' }} />
                </div>
                <button onClick={() => effectivenessMutation.mutate({ id: selectedCapa.id, scheduledDate: effectivenessDate, notes: effectivenessNotes })} disabled={!effectivenessDate || effectivenessMutation.isPending} style={{ fontSize:11, padding:'6px 12px', borderRadius:6, background:'#16A34A', color:'#fff', border:'none', cursor:'pointer', fontWeight:600, whiteSpace:'nowrap' }}>
                  Record Check
                </button>
              </div>
              <textarea rows={2} placeholder="Effectiveness check notes..." value={effectivenessNotes} onChange={(e) => setEffectivenessNotes(e.target.value)} style={{ fontSize:12, padding:'6px 8px', borderRadius:6, border:'1px solid #BBF7D0', resize:'none', outline:'none', width:'100%', marginTop:6, boxSizing:'border-box' }} />
            </div>

            <AuditTrail
              trail={selectedCapa.auditTrail}
              createdBy={selectedCapa.createdBy}
              createdAt={selectedCapa.createdAt}
              updatedBy={selectedCapa.updatedBy}
              updatedAt={selectedCapa.updatedAt}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
